import React, { useState } from 'react';
import Button from '../common/Button';
import FormField from '../common/FormField';

import { DonationDTO } from '../../services/apiClient';

interface Props {
  donation: DonationDTO;
  onChange: (updated: Partial<DonationDTO>) => void;
  onNext: () => void;
  debitOnly?: boolean;
}

const DonationOptions: React.FC<Props> = ({
  donation, onChange, onNext, debitOnly = false
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const quickAmounts =
    donation.frequency === 'once' ? [1000, 2500, 5000] : [100, 150, 200];

  const handleNext = () => {
    if (!donation.amount || donation.amount <= 0) {
      setError('Please enter a valid donation amount');
      return;
    }

    if (donation.frequency === 'monthly') {
      if (!donation.dayOfMonth || donation.dayOfMonth < 1 || donation.dayOfMonth > 31) {
        setError('Please select a valid debit order day');
        return;
      }
    }

    setError(undefined);
    onNext();
  };

  const getOrdinalSuffix = (n: number) => {
    if (n >= 11 && n <= 13) return 'th';
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return (
    <div>
      {!debitOnly && (
        <div className="text-left">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
            Choose donation frequency
          </label>
          <div className="flex gap-4 mb-4">
            <Button
              type="button"
              variant={donation.frequency === 'monthly' ? 'primary' : 'secondary'}
              onClick={() => {
                onChange({ frequency: 'monthly', amount: 150 });
              }}
            >
              Monthly
            </Button>

            <Button
              type="button"
              variant={donation.frequency === 'once' ? 'primary' : 'secondary'}
              onClick={() => {
                onChange({ frequency: 'once', amount: 2500, dayOfMonth: 1 });
              }}
            >
              Once Off
            </Button>
          </div>
        </div>
      )}

      <div className="text-left">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
          Choose an amount (ZAR)
        </label>
        <div className="flex gap-3 mb-4">
          {quickAmounts.map((val) => (
            <Button
              type="button"
              key={val}
              variant={donation.amount === val ? 'primary' : 'secondary'}
              onClick={() => onChange({ amount: val })}
              className="flex-1"
            >
              R{val}
            </Button>
          ))}
        </div>
      </div>

      <FormField
        id="amount"
        type="number"
        label="Adjust Support Amount (ZAR)"
        value={donation.amount}
        onChange={(e) => onChange({ amount: Number(e.target.value) })}
        error={error}
      />
      {donation.frequency === 'monthly' && (
        <FormField
          id="dayOfMonth"
          label="Debit Order Day (1-31)"
          type="number"
          min={1}
          max={31}
          value={donation.dayOfMonth ?? ''}
          onChange={(e) => onChange({ dayOfMonth: Number(e.target.value) })}
          placeholder="E.g. 25"
          helpText={
            donation.dayOfMonth
              ? `${donation.dayOfMonth}${getOrdinalSuffix(donation.dayOfMonth)} of each month`
              : undefined
          }
        />
      )}

      <div className="flex justify-end">
        <Button
          onClick={handleNext}
          variant="primary"
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default DonationOptions;
